/**
 * notificationRuleGuards.js — the pure condition-tree checks behind the
 * NotificationRuleWizard's "Conditions" step, kept out of the .jsx so
 * `node --test` can exercise them.
 *
 * findDisallowedNode(tree, allowedKeys) walks a rule group and returns the
 * FIRST problem it meets, or null:
 *   { aggregate: true }          — a cross-case count node (banned in
 *                                  notification rules; the server rejects it)
 *   { field }                    — a leaf whose field the current kind /
 *                                  trigger gate does not allow (left over after
 *                                  switching kind or deselecting a change
 *                                  trigger). Mirrors conditionFieldError
 *                                  server-side.
 *   { nestedOutstanding: true }  — an outstandingRequirement node inside a
 *                                  SUB-group. The wizard edits these nodes in
 *                                  its own editor as TOP-LEVEL members only
 *                                  (RuleBuilder has no row for them): nested,
 *                                  one would fall through to the field-
 *                                  condition editor and be rewritten on the
 *                                  first edit, so it is rejected up front with
 *                                  a clear message instead.
 */
import { isOutstandingRequirement } from '../utils/ruleFields.js';

export const NESTED_OUTSTANDING_MESSAGE =
  'Outstanding-requirement conditions can only sit in the top-level group — move the nested one up (or remove it).';

export function findDisallowedNode(node, allowedKeys, depth = 0) {
  if (!node || typeof node !== 'object') return null;
  if (node.type === 'aggregate') return { aggregate: true };
  if (node.type === 'fileCount') return null;
  if (isOutstandingRequirement(node)) {
    // depth 0 = the root group itself, 1 = a direct member of it (allowed),
    // 2+ = inside a sub-group (not editable by the wizard → rejected).
    return depth > 1 ? { nestedOutstanding: true } : null;
  }
  if (Array.isArray(node.rules)) {
    for (const child of node.rules) {
      const bad = findDisallowedNode(child, allowedKeys, depth + 1);
      if (bad) return bad;
    }
    return null;
  }
  return node.field != null && !allowedKeys.has(node.field) ? { field: node.field } : null;
}
